import {useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {motion, useReducedMotion} from "motion/react";
import {useMe} from "../common/useMe";

type GreetingOption = {
    id: string;
    title: string;
    subtitle: string;
};

const RECENT_GREETING_IDS_KEY = "home.recentGreetingIds";
const RECENT_GREETING_LIMIT = 3;

const getRecentGreetingIds = (): string[] => {
    if (typeof window === "undefined") return [];

    try {
        const storedIds = JSON.parse(window.localStorage.getItem(RECENT_GREETING_IDS_KEY) ?? "[]");

        return Array.isArray(storedIds)
            ? storedIds.filter((id): id is string => typeof id === "string")
            : [];
    } catch {
        return [];
    }
};

const selectGreetingId = (options: GreetingOption[]) => {
    const recentIds = getRecentGreetingIds();
    const freshOptions = options.filter(({id}) => !recentIds.includes(id));
    const availableOptions = freshOptions.length > 0 ? freshOptions : options;
    const selectedIndex = Math.floor(Math.random() * availableOptions.length);

    return availableOptions[selectedIndex]?.id ?? options[0]?.id ?? "";
};

const rememberGreetingId = (id: string) => {
    if (typeof window === "undefined" || !id) return;

    try {
        const recentIds = getRecentGreetingIds();
        const updatedIds = [id, ...recentIds.filter((recentId) => recentId !== id)]
            .slice(0, RECENT_GREETING_LIMIT);

        window.localStorage.setItem(RECENT_GREETING_IDS_KEY, JSON.stringify(updatedIds));
    } catch {
        // A greeting should still render when browser storage is unavailable.
    }
};

const WelcomeMessage = () => {
    const {t} = useTranslation();
    const {user} = useMe();
    const prefersReducedMotion = useReducedMotion();
    const name = user?.firstName?.trim() || t("there");
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay();
    let greetingOptions: GreetingOption[];

    if (hour < 5 || hour >= 22) {
        greetingOptions = [
            {
                id: "late-night-working",
                title: t("Working late, {{name}}?", {name}),
                subtitle: t("Everything you need for the late shift is here."),
            },
            {
                id: "late-night-calm",
                title: t("Good evening, {{name}}", {name}),
                subtitle: t("Let's keep the night calm and organized."),
            },
            {
                id: "late-night-ready",
                title: t("Still going, {{name}}?", {name}),
                subtitle: t("Your workspace is ready whenever you are."),
            },
        ];
    } else if (hour < 8) {
        greetingOptions = [
            {
                id: "early-morning-start",
                title: t("Early start, {{name}}", {name}),
                subtitle: t("Everything you need is ready for the day ahead."),
            },
            {
                id: "early-morning-moving",
                title: t("Good morning, {{name}}", {name}),
                subtitle: t("Let's get the day moving smoothly."),
            },
            {
                id: "early-morning-ready",
                title: t("The day is just getting started, {{name}}", {name}),
                subtitle: t("Your workspace is ready when you are."),
            },
        ];
    } else if (hour < 12) {
        greetingOptions = [
            {
                id: "morning-overview",
                title: t("Good morning, {{name}}", {name}),
                subtitle: t("Here's what is happening across your workspace."),
            },
            {
                id: "morning-welcome",
                title: t("Welcome back, {{name}}", {name}),
                subtitle: t("Let's keep today's operations moving."),
            },
            {
                id: "morning-ready",
                title: t("Ready for the day, {{name}}?", {name}),
                subtitle: t("Your apps are lined up and ready."),
            },
        ];
    } else if (hour < 14) {
        greetingOptions = [
            {
                id: "midday-overview",
                title: t("Good afternoon, {{name}}", {name}),
                subtitle: t("Here's the latest from your workspace."),
            },
            {
                id: "midday-attention",
                title: t("Halfway through the day, {{name}}", {name}),
                subtitle: t("Let's see what needs your attention."),
            },
            {
                id: "midday-moving",
                title: t("Keeping things moving, {{name}}?", {name}),
                subtitle: t("Your workspace is ready for the next task."),
            },
        ];
    } else if (hour < 18) {
        greetingOptions = [
            {
                id: "afternoon-momentum",
                title: t("Good afternoon, {{name}}", {name}),
                subtitle: t("Let's keep the momentum going."),
            },
            {
                id: "afternoon-overview",
                title: t("Nice to see you, {{name}}", {name}),
                subtitle: t("Here's a quick look at what is happening."),
            },
            {
                id: "afternoon-next",
                title: t("Ready for the next task, {{name}}?", {name}),
                subtitle: t("Everything is right where you left it."),
            },
        ];
    } else {
        greetingOptions = [
            {
                id: "evening-overview",
                title: t("Good evening, {{name}}", {name}),
                subtitle: t("Here's what is happening across your workspace."),
            },
            {
                id: "evening-smooth",
                title: t("Finishing the day strong, {{name}}?", {name}),
                subtitle: t("Let's keep the evening running smoothly."),
            },
            {
                id: "evening-ready",
                title: t("Welcome back, {{name}}", {name}),
                subtitle: t("Your workspace is ready for the evening."),
            },
        ];
    }

    if (day === 6) {
        greetingOptions.push(
            {
                id: "saturday-service",
                title: t("Saturday service is in motion, {{name}}", {name}),
                subtitle: t("Here's what needs your attention today."),
            },
            {
                id: "saturday-smooth",
                title: t("Keeping Saturday running smoothly, {{name}}?", {name}),
                subtitle: t("Your workspace is ready for the next task."),
            },
        );
    } else if (day === 0) {
        greetingOptions.push(
            {
                id: "sunday-operations",
                title: t("Sunday operations are underway, {{name}}", {name}),
                subtitle: t("Let's keep today's service on track."),
            },
            {
                id: "sunday-ready",
                title: t("Ready for Sunday, {{name}}?", {name}),
                subtitle: t("Everything you need is right here."),
            },
        );
    }

    const [greetingId] = useState(() => selectGreetingId(greetingOptions));
    const greeting = greetingOptions.find(({id}) => id === greetingId) ?? greetingOptions[0];

    useEffect(() => {
        rememberGreetingId(greeting.id);
    }, [greeting.id]);

    const containerMotion = prefersReducedMotion
        ? {}
        : {
            initial: {opacity: 0, y: 8},
            animate: {opacity: 1, y: 0},
            transition: {duration: 0.28, ease: "easeOut" as const},
        };
    const waveMotion = prefersReducedMotion
        ? {}
        : {
            initial: {rotate: 0, scale: 0.95},
            animate: {
                rotate: [0, 14, -8, 10, 0],
                scale: [0.95, 1.05, 1],
            },
            transition: {
                delay: 0.2,
                duration: 0.65,
                ease: "easeOut" as const,
            },
        };
    const textMotion = prefersReducedMotion
        ? {}
        : {
            initial: {opacity: 0, y: 4},
            animate: {opacity: 1, y: 0},
            transition: {delay: 0.08, duration: 0.25, ease: "easeOut" as const},
        };

    return (
        <Stack
            component={motion.div}
            direction="row"
            alignItems="flex-start"
            gap={1.5}
            {...containerMotion}
        >
            <Box
                component={motion.span}
                aria-hidden="true"
                sx={{
                    fontSize: {xs: 24, md: 32},
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                    transformOrigin: "70% 70%",
                }}
                {...waveMotion}
            >
                👋
            </Box>

            <Stack gap={0.5}>
                <Typography
                    component={motion.h2}
                    variant="h5"
                    fontWeight={600}
                    lineHeight={1.2}
                    {...textMotion}
                >
                    {greeting.title}
                </Typography>

                <Typography
                    component={motion.p}
                    variant="body2"
                    color="text.secondary"
                    {...textMotion}
                >
                    {greeting.subtitle}
                </Typography>
            </Stack>
        </Stack>
    );
};

export default WelcomeMessage;
